<template>
  <div v-if="results">
    <Result :results="results" :csvFile="csvFile"></Result>
  </div>
  <div class="mt-4" v-else>
    <b-container class="w-75">
      <div class="bg-white p-4 rounded-lg bordered">
        <h5><b-icon-info-circle-fill variant="primary" /> Uploading a CSV file</h5>
        <p>The file should contain a column with Caller ID, Date/Time details and Conversion
          Data. Conversion Data can be your call outcomes, transaction values or CRM events. The more detail you can
          share, the better.
        </p>
        <b-row align-v="center" class="text-center mt-5">
          <b-col cols="3">
            <b-avatar variant="light" size="50">
              <b-icon-telephone-fill font-scale="2"></b-icon-telephone-fill>
            </b-avatar>
            <p class="h5 mt-2">Caller ID</p>
          </b-col>
          <b-col><p class="h3 mt-2"><b>+</b></p></b-col>
          <b-col cols="3">
            <b-avatar variant="light" size="50">
              <b-icon-calendar2-check-fill font-scale="2"></b-icon-calendar2-check-fill>
            </b-avatar>
            <p class="h5 mt-2">Date &amp; Time</p>
          </b-col>
          <b-col><p class="h3 mt-2"><b>+</b></p></b-col>
          <b-col cols="3">
            <b-avatar variant="light" size="50">
              <b-icon-cash-stack font-scale="2" ></b-icon-cash-stack>
            </b-avatar>
            <p class="h5 mt-2">Transaction or Outcome</p>
          </b-col>
        </b-row>
      </div>
      <void-triggers class="mt-3" />
    </b-container>
    <b-container class="w-50 mt-4">
      <b-form-group :state="fileInputState">
        <template #invalid-feedback>
          <div class="d-flex file-error w-100 justify-content-center">
            <span class="bg-danger p-2 text-white rounded" v-test="'file error'">
              {{ fileMessage }}
            </span>
          </div>
        </template>
        <b-form-file
          accept=".csv"
          ref="file-input"
          id="imfile"
          class="file-input"
          v-model="file"
          :disabled="isLoading"
          @input="processFile"
          :state="fileInputState"
        >
          <template slot="placeholder">
            <div class="uploadPlaceholder">
              <h2><b-icon-cloud-upload /></h2>
              <strong>Click here to select your .csv file</strong>
              Alternatively, drag and drop a file here
            </div>
          </template>
          <template slot="file-name" slot-scope="scope">
            <div class="uploadPlaceholder">
              <h2><b-icon-cloud-upload variant="primary" /></h2>
              <strong v-for="name in scope.names" :key="name">{{ name }}</strong>
              <br>
            </div>
            <div class="loading" v-if="isLoading">
              <b-spinner></b-spinner>
            </div>
          </template>
          <template slot="drop-placeholder">
            <div class="uploadPlaceholder">
              <h2><b-icon-cloud-upload variant="primary" /></h2>
              <strong>Drop your .csv file here</strong>
              <br>
            </div>
          </template>
        </b-form-file>
      </b-form-group>
    </b-container>
  </div>
</template>

<script lang="ts">
import { Component, Vue } from 'vue-property-decorator'
import Result from '@/components/Result.vue'
import {
  AvatarPlugin, BIcon, BIconCalendar2CheckFill, BIconCloudUpload, BIconCreditCard, BIconInfoCircleFill,
  BIconTelephoneFill, BIconCashStack,
  FormFilePlugin,
  FormGroupPlugin,
  FormPlugin,
  SpinnerPlugin, ToastPlugin
} from 'bootstrap-vue'
import VoidTriggers from '@/components/VoidTriggers.vue'
import { get } from 'lodash'
import { FileError } from '@/store'

Vue.use(FormPlugin)
Vue.use(SpinnerPlugin)
Vue.use(FormFilePlugin)
Vue.use(FormGroupPlugin)
Vue.use(AvatarPlugin)
Vue.use(ToastPlugin)

@Component({
  components: {
    VoidTriggers,
    Result,
    BIconTelephoneFill,
    BIconCloudUpload,
    BIconCalendar2CheckFill,
    BIcon,
    BIconCreditCard,
    BIconInfoCircleFill,
    BIconCashStack
  }
})
export default class FileUpload extends Vue {
  private file = null
  private maxValidationRows = 20
  private isLoading = false

  created () {
    this.$mixpanelService.startTimedEvent('settings.smartMatch.view', {}, true)
  }

  appDeactivated () {
    this.$mixpanelService.trackTimedEvent('settings.smartMatch.view')
  }

  get fileInputState () {
    if (this.$store.state.fileError === FileError.None) {
      return null
    }

    return false
  }

  get fileMessage () {
    if (this.$store.state.fileError === FileError.InvalidRowLength) {
      return 'Some of your CSV rows have an incorrect length according to the number of headers provided. Please check the file and try again.'
    }

    if (this.$store.state.fileError === FileError.Unknown) {
      return 'There was a problem processing your CSV. Please check the file and try again.'
    }

    if (this.$store.state.fileError === FileError.InvalidHeaders) {
      return `The CSV you selected contained the following invalid headers: ${this.$store.state.invalidHeaders.join(', ')}.`
    }
  }

  get installationId () {
    return this.$infinityAuth.getInstallation().getId()
  }

  get results () {
    return this.$store.state.fileValidation
  }

  get csvFile () {
    return this.$store.state.csvFile
  }

  async processFile () {
    if (this.file !== null) {
      await this.$store.dispatch('reset')

      this.isLoading = true

      try {
        await this.$store.dispatch('prepareForUpload', { file: this.file, maxRows: this.maxValidationRows })

        if (this.$store.state.fileError !== FileError.None) {
          this.isLoading = false

          return
        }

        await this.$router.push('/mapping')
      } catch (e) {
        const message = get(e, 'response.data.error', 'An unknown error occurred, please try again')

        this.file = null
        this.$bvToast.toast(message, {
          title: 'Error',
          variant: 'danger',
          autoHideDelay: 5000
        })
      }
    }

    this.isLoading = false
  }

  doReset () {
    this.isLoading = false

    this.$store.dispatch('reset')
  }
}
</script>

<style lang="scss">
  .av-border {
    border: 1px solid #000;
  }

  .file-error {
    margin-top: 7rem;
  }

  .file-input {
    &.is-invalid {
      .custom-file-label {
        border: 1px solid $color-negative;
      }
    }

    .custom-file-label {
      text-align: center;
      padding-top: 5rem;
      padding-bottom: 5rem;
      display: flex;
      border: none;
      box-shadow: $box-shadow;
      border-radius: $border-radius-lg;
      cursor: pointer;
      position: relative;
      transition: box-shadow 0.2s ease-in-out;

      &:hover {
        box-shadow: $box-shadow-lg;
      }

      &:after {
        display: none;
      }

      >span {
        width: 100%;
        height: 100%;
      }

      &.loading {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(255,255,255, 0.7);
        display: flex;
        align-items: center;
        justify-content: center;
      }
    }

    input[disabled] + label {
      cursor: not-allowed;
    }
  }

  .uploadPlaceholder {
    display: flex;
    width: 100%;
    height: 100%;
    flex-direction: column;
    justify-content: center;
    align-items: center;
  }
</style>
